import { ChangeEvent, forwardRef, memo, useCallback } from 'react';
import { toNumeric } from './number-input.utils';
import { NumberInputProps } from './number-input.types';
import { Input } from '../input';

/**
 * Числовое поле. Намеренно не type="number": там не работает maxLength,
 * колесо мыши молча меняет значение при прокрутке страницы, а невалидный ввод
 * даёт пустую строку — «abc» не отличить от «». Берём текстовое поле
 * с числовой клавиатурой и фильтруем сами.
 */
export const NumberInput = memo(
  forwardRef<HTMLInputElement, NumberInputProps>((props, ref) => {
    const { decimals = 0, maxLength, onChange, ...rest } = props;

    const format = useCallback(
      (value: string) => {
        const numeric = toNumeric(value, decimals);

        return maxLength === undefined ? numeric : numeric.slice(0, maxLength);
      },
      [decimals, maxLength]
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const filtered = format(input.value);

        if (filtered !== input.value) {
          const caret = input.selectionStart ?? input.value.length;
          const validBefore = format(input.value.slice(0, caret)).length;

          input.value = filtered;
          input.setSelectionRange(validBefore, validBefore);
        }

        onChange?.(event);
      },
      [format, onChange]
    );

    return (
      <Input
        ref={ref}
        type="text"
        inputMode={decimals > 0 ? 'decimal' : 'numeric'}
        onChange={handleChange}
        {...rest}
      />
    );
  })
);

NumberInput.displayName = 'NumberInput';
